/**
 * Viraasat Parallax Engine
 *
 * Recreates the Framer Motion parallax scroll experience in vanilla JavaScript.
 * Features spring physics interpolation for smooth animations.
 *
 * Scroll Stages:
 * - Stage 1 (Space): 0-25% scroll
 * - Stage 2 (Aerial): 15-65% scroll
 * - Stage 3 (Ground): 55-100% scroll
 */

(function() {
  'use strict';

  class ViraasatParallax {
    constructor() {
      this.container = document.querySelector('[data-parallax-container]');
      if (!this.container) return;

      this.scrollHeight = 0;
      this.scrollProgress = 0;
      this.smoothProgress = 0;
      this.velocity = 0;
      this.isMobile = window.innerWidth < 768;

      // Spring physics parameters (matching Framer Motion)
      this.springStiffness = 70;
      this.springDamping = 25;
      this.timestep = 0.016; // ~60fps

      // Layer references
      this.layers = {
        space: document.querySelector('[data-layer="space"]'),
        aerial: document.querySelector('[data-layer="aerial"]'),
        ground: document.querySelector('[data-layer="ground"]'),
        titleText: document.querySelector('[data-layer="title-text"]'),
        aerialText: document.querySelector('[data-layer="aerial-text"]'),
        logo: document.querySelector('[data-layer="logo"]')
      };

      this.init();
    }

    init() {
      this.calculateScrollHeight();
      this.bindEvents();
      this.animate();
    }

    calculateScrollHeight() {
      const spacer = this.container.querySelector('[data-parallax-spacer]');
      if (spacer) {
        this.scrollHeight = spacer.offsetHeight;
      } else {
        this.scrollHeight = this.container.scrollHeight - window.innerHeight;
      }
    }

    bindEvents() {
      window.addEventListener('scroll', () => this.onScroll(), { passive: true });

      let resizeTimeout;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          this.isMobile = window.innerWidth < 768;
          this.calculateScrollHeight();
        }, 100);
      });
    }

    onScroll() {
      const scrollY = window.scrollY;
      const containerTop = this.container.offsetTop;
      const relativeScroll = scrollY - containerTop;

      this.scrollProgress = Math.min(Math.max(relativeScroll / this.scrollHeight, 0), 1);
    }

    /**
     * Spring physics update
     * Creates smooth, organic motion similar to Framer Motion's useSpring
     */
    updateSpring() {
      const delta = this.scrollProgress - this.smoothProgress;
      const spring = this.springStiffness * delta;
      const damper = this.springDamping * this.velocity;

      this.velocity += (spring - damper) * this.timestep;
      this.smoothProgress += this.velocity * this.timestep;

      // Clamp to valid range
      this.smoothProgress = Math.min(Math.max(this.smoothProgress, 0), 1);
    }

    /**
     * Multi-point interpolation
     * Replicates Framer Motion's useTransform with multiple input/output ranges
     *
     * @param {number} progress - Current scroll progress (0-1)
     * @param {number[]} inputRange - Array of scroll positions
     * @param {number[]} outputRange - Array of output values
     * @returns {number} Interpolated value
     */
    interpolate(progress, inputRange, outputRange) {
      if (progress <= inputRange[0]) return outputRange[0];
      if (progress >= inputRange[inputRange.length - 1]) return outputRange[outputRange.length - 1];

      for (let i = 0; i < inputRange.length - 1; i++) {
        if (progress >= inputRange[i] && progress <= inputRange[i + 1]) {
          const t = (progress - inputRange[i]) / (inputRange[i + 1] - inputRange[i]);
          // Linear interpolation between adjacent points
          return outputRange[i] + (outputRange[i + 1] - outputRange[i]) * t;
        }
      }

      return outputRange[outputRange.length - 1];
    }

    /**
     * Update all parallax layers based on scroll progress
     */
    updateLayers() {
      const p = this.smoothProgress;

      // ==========================================
      // STAGE 1: THE COSMOS (0% - 25%)
      // ==========================================
      const spaceOpacity = this.interpolate(p, [0, 0.25], [1, 0]);
      const spaceScale = this.interpolate(p, [0, 0.25], [1, 1.5]);
      this.applyTransform(this.layers.space, spaceOpacity, spaceScale);

      // ==========================================
      // STAGE 2: THE AERIAL VIEW (15% - 65%)
      // ==========================================
      const aerialOpacity = this.interpolate(p, [0.15, 0.3, 0.5, 0.65], [0, 1, 1, 0]);
      const aerialMaxScale = this.isMobile ? 1.5 : 2.5;
      const aerialScale = this.interpolate(p, [0.15, 0.65], [1.1, aerialMaxScale]);
      this.applyTransform(this.layers.aerial, aerialOpacity, aerialScale);

      // ==========================================
      // STAGE 3: THE GROUND BATTLE (55% - 100%)
      // ==========================================
      const groundOpacity = this.interpolate(p, [0.55, 0.7], [0, 1]);
      const groundScale = this.interpolate(p, [0.55, 1], [1.1, 1]);
      this.applyTransform(this.layers.ground, groundOpacity, groundScale);

      // ==========================================
      // TEXT: TITLE (Space Phase)
      // ==========================================
      const titleOpacity = this.interpolate(p, [0, 0.15], [1, 0]);
      const titleY = this.interpolate(p, [0, 0.2], [0, -50]);
      this.applyTextTransform(this.layers.titleText, titleOpacity, 1, titleY);

      // Logo follows title opacity
      if (this.layers.logo) {
        this.layers.logo.style.opacity = titleOpacity;
      }

      // ==========================================
      // TEXT: AERIAL (Battle Phase)
      // ==========================================
      const aerialTextInputRange = this.isMobile
        ? [0.22, 0.30, 0.45, 0.55]
        : [0.25, 0.35, 0.45, 0.55];
      const aerialTextOpacity = this.interpolate(p, aerialTextInputRange, [0, 1, 1, 0]);

      const aerialTextScaleRange = this.isMobile ? [0.22, 0.55] : [0.25, 0.55];
      const aerialTextScale = this.interpolate(p, aerialTextScaleRange, [0.8, 1.2]);
      this.applyTextTransform(this.layers.aerialText, aerialTextOpacity, aerialTextScale, 0);
    }

    /**
     * Apply opacity and scale transform to an element
     */
    applyTransform(element, opacity, scale) {
      if (!element) return;
      element.style.opacity = opacity;
      element.style.transform = `scale(${scale})`;
    }

    /**
     * Apply opacity, scale, and translateY to text elements
     */
    applyTextTransform(element, opacity, scale, translateY) {
      if (!element) return;
      element.style.opacity = opacity;
      element.style.transform = `scale(${scale}) translateY(${translateY}%)`;
    }

    /**
     * Animation loop using requestAnimationFrame
     */
    animate() {
      this.updateSpring();
      this.updateLayers();
      requestAnimationFrame(() => this.animate());
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new ViraasatParallax());
  } else {
    new ViraasatParallax();
  }
})();
